import {
    Color3,
    DirectionalLight,
    Engine,
    HemisphericLight,
    MeshBuilder,
    PBRMetallicRoughnessMaterial,
    Scene,
    ShadowGenerator,
    Vector3
} from '@babylonjs/core';
import HavokPhysics from '@babylonjs/havok';

import { ThirdPersonCameraController } from '../Systems/CombatSystem/Modules/Camera/Classes/ThirdPersonCameraController';
import { PlayerMovementController } from '../Systems/CombatSystem/Modules/Movement/Classes/PlayerMovementController';
import { AnimationController } from '../Systems/CombatSystem/Modules/Animation/Classes/AnimationController';
import { CrosshairController } from '../Systems/CombatSystem/Modules/Weapons/Classes/CrosshairController';
import { WeaponController } from '../Systems/CombatSystem/Modules/Weapons/Classes/WeaponController';
import { PlayerCombatController } from '../Systems/CombatSystem/Modules/Combat/Classes/PlayerCombatController';
import { LobbyHubController } from '../Systems/LobbySystem/Modules/Hub/Classes/LobbyHubController';

const canvas = document.getElementById('renderer') as HTMLCanvasElement;
canvas.width = window.innerWidth;
canvas.height = window.innerHeight;

const engine = new Engine(canvas);
engine.displayLoadingUI();

async function main() {
    const havokInstance = await HavokPhysics();
    const { HavokPlugin } = await import('@babylonjs/core/Physics/v2/Plugins/havokPlugin');
    const havokPlugin = new HavokPlugin(true, havokInstance);

    const scene = new Scene(engine);
    scene.enablePhysics(new Vector3(0, -9.81, 0), havokPlugin);

    const sun = new DirectionalLight('sun', new Vector3(-5, -10, 5).normalize(), scene);
    sun.position = sun.direction.negate().scaleInPlace(40);
    sun.intensity = 1.2;

    const shadowGenerator = new ShadowGenerator(1024, sun);
    shadowGenerator.useExponentialShadowMap = true;

    const hemiLight = new HemisphericLight('hemi', Vector3.Up(), scene);
    hemiLight.intensity = 0.4;

    const groundMat = new PBRMetallicRoughnessMaterial('groundMat', scene);
    groundMat.baseColor = new Color3(0.25, 0.28, 0.22);

    const ground = MeshBuilder.CreateGround('ground', { width: 100, height: 100 }, scene);
    ground.material = groundMat;
    ground.receiveShadows = true;

    const { PhysicsAggregate } = await import('@babylonjs/core/Physics/v2/physicsAggregate');
    const { PhysicsShapeType } = await import('@babylonjs/core/Physics/v2/IPhysicsEnginePlugin');
    new PhysicsAggregate(ground, PhysicsShapeType.BOX, { mass: 0 }, scene);

    for (let i = 0; i < 10; i++) {
        const boxMat = new PBRMetallicRoughnessMaterial('boxMat_' + i, scene);
        boxMat.baseColor = Color3.Random();

        const size = 0.5 + Math.random() * 1.5;
        const box = MeshBuilder.CreateBox('coverBox_' + i, { width: size, height: size * (0.5 + Math.random()), depth: size }, scene);
        box.material = boxMat;
        box.checkCollisions = true;
        shadowGenerator.addShadowCaster(box);
        box.position.copyFromFloats(
            (Math.random() - 0.5) * 30,
            size * 0.25 + Math.random() * 2,
            (Math.random() - 0.5) * 30
        );

        const boxAggregate = new PhysicsAggregate(box, PhysicsShapeType.BOX, { mass: 0 }, scene);
    }

    const { StandardMaterial } = await import('@babylonjs/core/Materials/standardMaterial');
    for (let i = 0; i < 4; i++) {
        const wallMat = new StandardMaterial('wallMat_' + i, scene);
        wallMat.diffuseColor = new Color3(0.35, 0.3, 0.25);
        wallMat.specularColor = new Color3(0.05, 0.05, 0.05);

        const wall = MeshBuilder.CreateBox('wall_' + i, { width: 20, height: 3, depth: 0.5 }, scene);
        wall.material = wallMat;
        wall.checkCollisions = true;
        shadowGenerator.addShadowCaster(wall);

        if (i === 0) wall.position = new Vector3(0, 1.5, -10);
        else if (i === 1) wall.position = new Vector3(0, 1.5, 10);
        else if (i === 2) { wall.position = new Vector3(-10, 1.5, 0); wall.rotation.y = Math.PI / 2; }
        else { wall.position = new Vector3(10, 1.5, 0); wall.rotation.y = Math.PI / 2; }

        new PhysicsAggregate(wall, PhysicsShapeType.BOX, { mass: 0 }, scene);
    }

    const capsuleMat = new PBRMetallicRoughnessMaterial('playerMat', scene);
    capsuleMat.baseColor = new Color3(0.3, 0.5, 0.9);
    capsuleMat.metallic = 0.1;
    capsuleMat.roughness = 0.6;

    const playerMesh = MeshBuilder.CreateCapsule('player', { height: 2, radius: 0.4 }, scene);
    playerMesh.material = capsuleMat;
    playerMesh.position = new Vector3(0, 2, 0);
    shadowGenerator.addShadowCaster(playerMesh);

    const game: any = {
        scene,
        engine,
        playerMesh,
        playerSlot: 'local',
        playerName: 'Player',
        mapName: 'arena',
        input: {},
    };

    const thirdPersonCamera = new ThirdPersonCameraController(game);
    const playerMovement = new PlayerMovementController(game);
    const animationController = new AnimationController(game);
    const crosshair = new CrosshairController(game);
    const weaponController = new WeaponController(game);
    const combatController = new PlayerCombatController(game);
    const lobbyHub = new LobbyHubController(game);

    game.thirdPersonCameraController = thirdPersonCamera;
    game.playerMovementController = playerMovement;
    game.animationController = animationController;
    game.crosshairController = crosshair;
    game.weaponController = weaponController;
    game.playerCombatController = combatController;

    thirdPersonCamera.init(scene, playerMesh);
    playerMovement.init(scene, playerMesh);
    crosshair.init();
    weaponController.init(scene, playerMesh);
    combatController.init();

    document.addEventListener('mousedown', (e) => {
        if (e.button === 0) {
            weaponController.startFiring();
        }
    });
    document.addEventListener('mouseup', (e) => {
        if (e.button === 0) {
            weaponController.stopFiring();
        }
    });
    document.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        crosshair.setAiming(true);
    });
    document.addEventListener('mouseup', (e) => {
        if (e.button === 2) {
            crosshair.setAiming(false);
        }
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'q' || e.key === 'Q') {
            weaponController.nextWeapon();
        }
    });

    function updateScene() {
        const deltaSeconds = engine.getDeltaTime() / 1000;
        thirdPersonCamera.update(deltaSeconds);
        playerMovement.update(deltaSeconds);
        crosshair.update(deltaSeconds);
        weaponController.update(deltaSeconds);
        combatController.update(deltaSeconds);
    }

    scene.executeWhenReady(() => {
        engine.loadingScreen.hideLoadingUI();
        scene.onBeforeRenderObservable.add(() => updateScene());
        engine.runRenderLoop(() => scene.render());
    });
}

main().catch((err) => {
    console.error('7ENGINE init failed:', err);
    engine.loadingScreen.hideLoadingUI();
});

window.addEventListener('resize', () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    engine.resize();
});
